import React, { useState, useEffect } from 'react';
import { Col, Row, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
//api
import { deleteAffiliation } from 'api/company';
//components
import { SelectInputCustom } from 'components/input/index';
import Button from 'components/backoffice/buttonCustom';
import Input from 'components/backoffice/customInput';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';


const AffiliationForm = ({
  isEditing,
  onSubmitForm,
  defaultValues,
  beforeTitle,
  loading,
}) => {
  const { t } = useTranslation(['backoffice', 'home']);
  const navigate = useNavigate();
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const {
    formState: { errors },
    setValue,
    reset,
    control,
    handleSubmit,
  } = useForm();

  const statusOptions = [
    { id: 'pending', value: t('pending', { ns: 'home' }) },
    { id: 'accepted', value: t('accepted', { ns: 'home' }) },
    { id: 'rejected', value: t('rejected', { ns: 'home' }) },
  ];

  useEffect(() => {
    if (!isEditing) reset(defaultValues);
  }, [reset, defaultValues]);

  useEffect(() => {
    if (defaultValues) {
      setValue('name', defaultValues.name || '');
      setValue('address', defaultValues.address || '');
      setValue('phone', defaultValues.phone || '');
      setValue('email', defaultValues.email || '');
      setValue('request_description', defaultValues.request_description || '');
      setValue('status', defaultValues.status || 'pending');
    }
  }, [defaultValues, setValue]);

  const handleDeleteAffiliation = async (id) => {
    setDeleting(true);
    try {
      await deleteAffiliation(id);
      setOpenModalDelete(false);
      toast.success(t('eliminadocorrectamente'));
      navigate('/intranet/affiliation-request', { replace: true });
    } catch (error) {
      console.error('Error al eliminar la afiliación:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-5">
      <Form onSubmit={handleSubmit(onSubmitForm)}>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0">
            <Controller
              name="name"
              rules={{ required: { value: true, message: t('camporequerido') } }}
              control={control}
              render={({ field }) => (
                <Input
                  type="text"
                  label={t('name')}
                  {...field}
                  placeholder={t('name')}
                />
              )}
            />
            {errors?.name && <small className="text-danger">{errors.name.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={6} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name="address"
              rules={{ required: { value: true, message: t('camporequerido') } }}
              control={control}
              render={({ field }) => (
                <Input
                  type="text"
                  label={t('direccion')}
                  {...field}
                  placeholder={t('direccion')}
                />
              )}
            />
            {errors?.address && <small className="text-danger">{errors.address.message}</small>}
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0">
            <Controller
              name="phone"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
                pattern: {
                  value: /^[0-9]+$/,
                  message: t('solonumeros'),
                },
                minLength: {
                  value: 6,
                  message: t('minimumLength'),
                },
                maxLength: {
                  value: 15,
                  message: t('maximumLength'),
                },
              }}
              render={({ field }) => (
                <Input
                  type="text"
                  label={t('telefono')}
                  {...field}
                  placeholder={t('telefono')}
                />
              )}
            />
            {errors?.phone && <small className="text-danger">{errors.phone.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={6} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name="email"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                  message: t('incorrectFormat'),
                },
              }}
              render={({ field }) => (
                <Input
                  type="email"
                  label={t('orderTrans.email')}
                  {...field}
                  placeholder={t('orderTrans.email')}
                />
              )}
            />
            {errors?.email && <small className="text-danger">{errors.email.message}</small>}
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0">
            <Controller
              name="request_description"
              control={control}
              render={({ field }) => (
                <Input
                  as="textarea"
                  rows={4}
                  label={t('requestDescription')}
                  {...field}
                  placeholder={t('requestDescription')}
                />
              )}
            />
          </Col>
          <Col sm={12} md={12} lg={6} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <SelectInputCustom
                  choices={[{ id: '', value: t('select') }, ...statusOptions]}
                  label={t('exports.state')}
                  value={field.value}
                  onCheckedItem={(value) => {
                    if (value === '') return;
                    field.onChange(value);
                  }}
                />
              )}
            />
          </Col>
        </Row>
      </Form>
      <div className="w-90 my-4 d-flex justify-content-end gap-3">
        <Button
          color="primary"
          onClick={handleSubmit(onSubmitForm)}
          loading={loading}
        >
          {t('save')} <SpinnerComponent loading={loading} />
        </Button>
        {isEditing && (
          <Button
            color="secondary"
            onClick={() => setOpenModalDelete(true)}
            loading={loading}
          >
            {t('delete')}
          </Button>
        )}
      </div>
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={() => handleDeleteAffiliation(defaultValues.id)}
        deleteTitle={t('delete_affiliation')}
        beforeTitle={beforeTitle ? beforeTitle : t('edit', { title: defaultValues?.name })}
        deleting={deleting}
      />
    </div>
  );
};

AffiliationForm.propTypes = {
  onSubmitForm: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  isEditing: PropTypes.bool.isRequired,
  beforeTitle: PropTypes.string,
  loading: PropTypes.bool,
};

export default AffiliationForm;